/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/** * MapHelper - Utility functions for map-related operations
 * Migrated from sarsaparilla to provide consistent map functionality across the application
 */

export const circleIconsColors = [
    ['camping_pin', '#4a77b4'],
    ['camping_pin_disabled', '#afbec8'],
    ['camping_pin_active', '#c3512c'],
    ['dayUse_pin', '#7fb135'],
    ['gateway_pin', '#efaa00'],
    ['permit_pin', '#d27a56'],
    ['recArea_pin', '#efaa00'],
    ['poi_pin', '#5abf95'],
    ['tour_pin', '#93348c'],
    ['ticketFacility_pin', '#93348c'],
    ['pass_pin', '#3e4ded'],
    ['activity_pass_pin', '#0D7EA2'],
    ['activity_pass_pin_disabled', '#afbec8'],
    ['activity_pass_pin_active', '#c3512c'],
    ['venue_wedding_pin', '#731F44'],
    ['venue_small_gathering_pin', '#731F44'],
    ['venue_shooting_pin', '#731F44'],
    ['venue_other_pin', '#731F44'],
    ['venue_large_reception_pin', '#731F44'],
    ['venue_hunting_pin', '#731F44'],
    ['venue_golfing_pin', '#731F44'],
    ['venue_athletic_activity_pin', '#731F44'],
];

export const amenityCategoryIcons = {
    amenities: {
        default: 'amenities',
    },
    toilets: {
        default: 'restrooms',
    },
    'bath / shower': {
        'solar shower': 'solar-showers',
        default: 'showers',
    },
    'waste management': {
        recycling: 'recycling',
        'dump station': 'sanitary-disposal-station',
        default: 'trash-dumpster',
    },
    'for purchase': {
        default: 'for-purchase',
    },
    parking: {
        default: 'parking',
    },
    'trails and paths': {
        default: 'trailhead',
    },
    'sports and rec': {
        courts: 'court',
        hiking: 'trailhead',
        fishing: 'fishing',
        default: 'playground',
    },
    'water access': {
        default: 'water-access',
    },
    'boat access': {
        default: 'boat-launch',
    },
    swimming: {
        default: 'swimming',
    },
    fishing: {
        default: 'fishing',
    },
    'horses & livestock': {
        default: 'horse-site',
    },
    'group amenities': {
        amphitheater: 'amphitheater',
        default: 'group-amenities',
    },
    campfire: {
        default: 'fire',
    },
    'outdoor dining': {
        'picnic table': 'picnic-table',
        'picnic area': 'picnic-shelter',
        default: 'picnic-shelter',
    },
    water: {
        'water spigot': 'water-hookup',
        default: 'drinking-water',
    },
    platform: {
        default: 'platform',
    },
    information: {
        'ranger station': 'ranger-station',
        'visitor center': 'cabin',
        entrance: 'entrance',
        'campground host': 'campground-host',
        default: 'info',
    },
    accessibility: {
        default: 'accessible',
    },
    shelters: {
        default: 'shelter',
    },
    automotive: {
        default: 'car',
    },
    'boat amenities': {
        default: 'boat-site',
    },
    communications: {
        default: 'communications',
    },
    'cultural / historic': {
        default: 'historic-feature',
    },
    entertainment: {
        default: 'entertainment',
    },
    'financial services': {
        default: 'financial-services',
    },
    'food and beverages': {
        default: 'cafe',
    },
    'health & safety': {
        default: 'health-and-safety',
    },
    lodging: {
        default: 'motel',
    },
    medical: {
        default: 'hospital',
    },
    'natural features': {
        default: 'viewing-area',
    },
    'rv amenities': {
        default: 'rv',
    },
    'special uses': {
        default: 'special-uses',
    },
    'to rent': {
        default: 'rental',
    },
    transportation: {
        default: 'transit',
    },
    'vending/shopping': {
        default: 'vending-shopping',
    },
};

export function getTargetUri(
    item,
    cmsDomain = process.env.SITE_ROOT,
    kbDomain = process.env.SN_KB_EXT_URL
) {
    // Handle campsite_id case first (special case not based on entity_type)
    if (item.campsite_id) {
        return `/camping/campsites/${item.campsite_id}`;
    }

    const { entity_type, entity_id, parent_entity_id, parent_id } = item;

    switch (entity_type) {
        case 'campground':
            if (item.reservable) {
                return `/camping/campgrounds/${entity_id}`;
            }
            // Non-reservable campgrounds fall through to default POI handling
            return `/camping/poi/${entity_id}`;

        case 'permit':
            return `/permits/${entity_id}`;

        case 'vehiclepermit':
            return `/vehiclepermits/${entity_id}`;

        case 'tour': {
            const parentId = parent_entity_id || parent_id;
            return `/ticket/${parentId}/ticket/${entity_id}`;
        }

        case 'ticketfacility':
            return `/ticket/facility/${entity_id}`;

        case 'timedentry_tour': {
            const parentId = parent_entity_id || parent_id;
            return `/timed-entry/${parentId}/ticket/${entity_id}`;
        }

        case 'timedentry':
            return `/timed-entry/${entity_id}`;

        case 'recarea':
            return `/gateways/${entity_id}`;

        case 'pass':
            return `/sitepass/${
                item.park_pass_facility_id ? item.park_pass_facility_id : entity_id
            }`;

        case 'activitypass':
            return `/activitypass/${entity_id}`;

        case 'treepermit':
            return `/tree-permits/${entity_id}`;

        case 'venuereservations':
            return `/venues/${entity_id}`;

        case 'venuereservations_venue': {
            const parentId = parent_entity_id || parent_id;
            return `/venues/${parentId}/details/${entity_id}`;
        }

        case 'program':
            return `/programs/${entity_id}`;

        case 'kb':
            // ServiceNow knowledge base
            return `${kbDomain}?id=kb_article&sys_id=${entity_id}`;

        case 'cms': {
            // Navigate to CMS article - the Drupal way
            const name = item.name.toLowerCase().split(' ').join('-');
            const type = item.content_type.toLowerCase().split('_').join('-');
            return `${cmsDomain}/articles/${type}/${name}/${entity_id}`;
        }

        case 'page':
        case 'interagencypass':
            return item.url;

        default:
            return `/camping/poi/${entity_id}`;
    }
}

export const getMapIcon = (item) => {
    let mapIcon;

    // Handle campsite_id case first (special case not based on entity_type)
    if (item.campsite_id) {
        mapIcon = 'camping_pin';
        if (item.type_of_use === 'Day') {
            mapIcon = 'dayUse_pin';
        }
    } else {
        switch (item.entity_type) {
            case 'campground':
                mapIcon = 'camping_pin';
                // use camping pin for all campgrounds but those with exclusively day-use campsites
                if (item.campsite_type_of_use) {
                    mapIcon = 'dayUse_pin';
                    item.campsite_type_of_use.forEach((type) => {
                        if (type !== 'Day') {
                            mapIcon = 'camping_pin';
                        }
                    });
                }
                break;

            case 'permit':
            case 'vehiclepermit':
            case 'treepermit':
                mapIcon = 'permit_pin';
                break;

            case 'tour':
            case 'timedentry_tour':
                mapIcon = 'tour_pin';
                break;

            case 'ticketfacility':
            case 'timedentry':
                mapIcon = 'ticketFacility_pin';
                break;

            case 'recarea':
                mapIcon = 'recArea_pin';
                break;

            case 'activitypass':
                mapIcon = 'activity_pass_pin';
                break;

            case 'venuereservations':
            case 'venuereservations_venue': {
                const venuePins = {
                    'wedding-ceremony': 'venue_wedding_pin',
                    'small-gathering': 'venue_small_gathering_pin',
                    shooting: 'venue_shooting_pin',
                    other: 'venue_other_pin',
                    'large-reception': 'venue_large_reception_pin',
                    hunting: 'venue_hunting_pin',
                    golfing: 'venue_golfing_pin',
                    'athletic-activity': 'venue_athletic_activity_pin',
                };

                const tag = item.venue_tags?.[0];
                mapIcon = venuePins[tag] || 'venue_other_pin';
                break;
            }

            default:
                mapIcon = 'poi_pin';
                break;
        }
    }

    if (item.availability === 'unavailable') {
        mapIcon += '_disabled';
    }

    return mapIcon;
};

export const kmsToMiles = (kms) => {
    if (typeof kms === 'number') {
        return (kms * 0.621371).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }
    return null;
};
